import { Box, Typography, makeStyles, Tabs, Tab } from "@material-ui/core";
import { MailOutline, PhoneOutlined } from "@material-ui/icons";
import dayjs from "dayjs";
import React, { useState, useMemo, FC, useEffect } from "react";
import { useHistory, useParams } from "react-router";
import { useRecoilValue } from "recoil";

import { CustomButton } from "@/components/Button";
import { LinkifiedText } from "@/components/LinkifiedText";
import { Loading } from "@/components/Loading";
import { AgentProfile } from "@/components/offer/AgentProfile";
import { OfferApproveModal } from "@/components/offer/ApproveModal";
import { OfferDetailContent } from "@/components/offer/Content";
import { PropertyAppraisedPrice } from "@/components/offer/PropertyAppraisedPrice";
import { ProposalLoadError } from "@/components/offer/ProposalLoadError";
import { ProposalNotFound } from "@/components/offer/ProposalNotFound";
import { SalesAchievementsCard } from "@/components/offer/SalesAchievementsCard";
import { StyledChip } from "@/components/offer/StrengthChip";
import { PLANNER_MATCH_CTA_LABEL } from "@/constants/plannerMatching";
import { useApproveAssesmentProposal } from "@/hooks/useApproveAssesmentProposal";
import { useGetAssessmentProposalDetails } from "@/hooks/useGetAssesmentProposalDetails";
import { useMasterAgentSearchTagMap } from "@/hooks/useGetMasterData";
import { useUpdateAssessmentRead } from "@/hooks/useUpdateAssesmentRead";
import { useUpdatePreferredContactSchedules } from "@/hooks/useUpdatePreferredContactSchedules";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { buildSupportedPropertyTypeLabels } from "@/utilities/agentSupportedPropertyTypes";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { isValidProposalId } from "@/utilities/proposalId";
import { UserState } from "@/view_models/atoms";

const useStyles = makeStyles(() => ({
  section: {
    "&:not(:first-child)": {
      marginTop: theme.spacing(3),
    },
  },
  salesAchievements: {
    "&:not(:first-child)": {
      marginTop: theme.spacing(3),
    },
    padding: theme.spacing(2),
    borderRadius: 8,
  },
  cardContainer: {
    display: "flex",
    gap: theme.spacing(1),
    overflowX: "auto",
    whiteSpace: "nowrap",
    paddingBottom: theme.spacing(1),
  },
  offerContent: {
    paddingBottom: 16,
    marginBottom: 16,
    marginTop: 16,
    backgroundColor: theme.palette.grey[100],
    borderRadius: 11,
    padding: theme.spacing(2),
  },
  agentContactContent: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 4,
  },
  agentContactLabel: {
    marginLeft: theme.spacing(1),
  },
  button: {
    margin: `${theme.spacing(4)} 0`,
    [theme.breakpoints.down("xs")]: {
      position: "fixed",
      bottom: 0,
      right: 0,
      left: 0,
      marginBottom: theme.spacing(4),
    },
  },
  tabsRoot: {
    width: "100%",
    borderRadius: 4,
    padding: theme.spacing(1),
  },
  tabsIndicator: {
    height: 3,
    borderRadius: 3,
  },
  tabRoot: {
    minWidth: 0,
    flex: 1,
    textTransform: "none",
    fontWeight: 700,
  },
}));

export const OfferAssessmentDetailPage: FC = () => {
  const { id } = useParams<{ id?: string }>();
  // 別IDではタブ・成立通知・承認中の非同期処理を新しい画面へ引き継がない。
  return <OfferAssessmentDetailContent key={id} />;
};

const OfferAssessmentDetailContent: FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const numId = Number(id);
  const validId = isValidProposalId(numId);

  const classes = useStyles();
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approvedAgentId, setApprovedAgentId] = useState<number | undefined>();
  const [tabIndex, setTabIndex] = useState(0);
  const { tagIdToNameMap } = useMasterAgentSearchTagMap();

  // 既読更新は副作用で一度だけ
  const updateAssessmentRead = useUpdateAssessmentRead();
  useEffect(() => {
    if (validId) updateAssessmentRead(numId);
  }, [validId, numId, updateAssessmentRead]);

  const {
    fetchAssessmentProposalById,
    proposalData,
    loading: fetching,
    notFound,
    loadError,
  } = useGetAssessmentProposalDetails(validId ? numId : undefined);

  const { approveAssesmentProposal, loading: approving } = useApproveAssesmentProposal();
  const { updatePreferredContactSchedules, loading: updatingPreferredContactSchedules } =
    useUpdatePreferredContactSchedules();

  const loading = useMemo(
    () => fetching || approving || updatingPreferredContactSchedules,
    [fetching, approving, updatingPreferredContactSchedules]
  );

  const user = useRecoilValue(UserState);

  const handleTabChange = (_event: React.ChangeEvent<unknown>, newValue: number) => {
    setTabIndex(newValue);
  };

  // 話を聞いてみるボタンの押下時
  const handleClickModalApprove = async () => {
    if (!validId) return;
    window.scrollTo(0, 0);
    try {
      await approveAssesmentProposal(numId);
      // adjust向けにマッチングイベント発火
      sendEventToAppMeasurementTool(AppEventTokenEnum.ApproveProposal);

      setApprovedAgentId(proposalData?.agent?.id);
      setIsApproveModalOpen(true);
      await fetchAssessmentProposalById();
    } catch {
      // 特に何もしない
    }
  };

  const formattedDate = proposalData?.proposal_date ? dayjs(proposalData.proposal_date).format("YYYY年MM月DD日") : "-";
  const representativeName = `${proposalData?.agent?.family_name ?? ""}${proposalData?.agent?.first_name ?? ""}`;
  const strengthChips = proposalData?.agent?.company?.company_agent_search_tag_ids;
  const supportedPropertyTypes = useMemo(() => {
    return buildSupportedPropertyTypeLabels(proposalData?.agent?.supported_property_types);
  }, [proposalData?.agent?.supported_property_types]);
  const isMatchButtonVisible = proposalData?.is_matchable !== false;

  return (
    <BaseTemplate headerTitle="査定メッセージ詳細" onBack={() => history.length > 1 ? history.goBack() : history.push("/offer")} isBackGroundWhite={true}>
      {loadError && proposalData && !loading && <ProposalLoadError compact onRetry={fetchAssessmentProposalById} />}
      {loading ? (
        <Loading />
      ) : loadError && !proposalData ? (
        <ProposalLoadError onRetry={fetchAssessmentProposalById} />
      ) : notFound || !proposalData ? (
        // 表示対象外・退会プランナー・削除済みなどで取得できない: 空カードではなく案内と一覧への導線を出す
        <ProposalNotFound onBackToList={() => history.push("/offer")} />
      ) : (
        <>
          <Box whiteSpace="pre-wrap">
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              classes={{ root: classes.tabsRoot, indicator: classes.tabsIndicator }}
              centered
            >
              <Tab label="メッセージ内容" classes={{ root: classes.tabRoot }} />
              <Tab label="認定プランナー情報" classes={{ root: classes.tabRoot }} />
            </Tabs>

            {/* タブ1: オファー内容 */}
            {tabIndex === 0 && (
              <Box className={classes.section}>
                {/* 日付 */}
                <Box display="flex" justifyContent="flex-end">
                  <Typography variant="caption" color="textSecondary">
                    {formattedDate}
                  </Typography>
                </Box>

                {/* プロフィール画像 */}
                <AgentProfile
                  profileImagePath={proposalData?.agent?.agent_profile_image?.image_url || ""}
                  companyImagePath={proposalData?.agent?.company?.company_logo_image?.image_url || ""}
                  companyName={proposalData?.agent?.company?.name || ""}
                  companyHomePageUrl={proposalData?.agent?.company?.home_page_url}
                  representativeName={representativeName}
                  supportedPropertyTypes={supportedPropertyTypes}
                />

                {/* 物件査定額 */}
                <Box className={classes.offerContent}>
                  <PropertyAppraisedPrice
                    proposalPrice={proposalData?.proposal_price || 0}
                    propertyName={proposalData?.property?.name || ""}
                  />
                </Box>

                {/* 提案メッセージ */}
                <Box mt={2}>
                  <Typography variant="body1">{`${user?.family_name ?? ""} ${user?.first_name ?? ""} 様`}</Typography>
                  <br />
                  <Typography variant="body1">
                    <LinkifiedText text={proposalData?.proposal_content} />
                  </Typography>
                </Box>
              </Box>
            )}

            {/* タブ2: 認定プランナー情報 */}
            {tabIndex === 1 && (
              <Box className={classes.section}>
                {/* 日付 */}
                <Box display="flex" justifyContent="flex-end">
                  <Typography variant="caption" color="textSecondary">
                    {formattedDate}
                  </Typography>
                </Box>

                {/* プロフィール画像 */}
                <AgentProfile
                  profileImagePath={proposalData?.agent?.agent_profile_image?.image_url || ""}
                  companyImagePath={proposalData?.agent?.company?.company_logo_image?.image_url || ""}
                  companyName={proposalData?.agent?.company?.name || ""}
                  companyHomePageUrl={proposalData?.agent?.company?.home_page_url}
                  representativeName={representativeName}
                  supportedPropertyTypes={supportedPropertyTypes}
                />

                {/* マッチング済みであれば、電話番号とメールアドレスを表示 */}
                {proposalData?.is_matched && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Box className={classes.agentContactContent}>
                      <Box display="flex" alignItems="center">
                        <PhoneOutlined color="primary" fontSize="small" />
                        <Typography variant="h4" className={classes.agentContactLabel}>
                          {proposalData?.agent.phone_number}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <MailOutline color="primary" fontSize="small" />
                        <Typography variant="h4" className={classes.agentContactLabel}>
                          {proposalData?.agent.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* 特に強い分野 */}
                {strengthChips?.length > 0 && (
                  <Box mt={2}>
                    <Box display="flex" flexWrap="wrap" mt={1}>
                      {strengthChips.map((tagId) => {
                        const tagName = tagIdToNameMap[tagId];
                        return <StyledChip label={tagName || `タグID: ${tagId}`} key={`strength-${tagId}`} />;
                      })}
                    </Box>
                  </Box>
                )}

                {/* プロフィール */}
                <Box className={classes.section}>
                  <Box mb={2} mt={2}>
                    <Typography variant="h4">私のプロフィール</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" color="textPrimary">
                      <LinkifiedText text={proposalData?.agent?.profile} />
                    </Typography>
                  </Box>
                </Box>

                {/* 直近の販売実績 */}
                {proposalData?.agent?.company?.sales_achievements?.length > 0 && (
                  <Box className={classes.salesAchievements}>
                    <Box mb={2} mt={2}>
                      <Typography variant="h4">直近の販売実績</Typography>
                    </Box>
                    <Box className={classes.cardContainer}>
                      {proposalData.agent.company.sales_achievements.map((achievement, index) => (
                        <SalesAchievementsCard
                          key={index}
                          profileImagePath={achievement.file_url || ""}
                          propertyName={achievement.achievement_name || "-"}
                          propertyArea={achievement.achievement_area || "-"}
                          propertyIncome={achievement.achievement_price || 0}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* 会社情報 */}
                <Box className={classes.section}>
                  <Box mb={2} mt={2}>
                    <Typography variant="h4">会社情報</Typography>
                  </Box>
                  {proposalData?.agent?.company?.achievements && (
                    <OfferDetailContent title="会社の実績" content={proposalData.agent.company.achievements} />
                  )}
                  {proposalData?.agent?.company?.strengths_of_properties_for_sale_and_purchase && (
                    <OfferDetailContent
                      title="販売物件/物件買取の強み"
                      content={proposalData.agent.company.strengths_of_properties_for_sale_and_purchase}
                    />
                  )}
                  {proposalData?.agent?.company?.strengths_of_partner_financial_institutions && (
                    <OfferDetailContent
                      title="提携金融機関"
                      content={proposalData.agent.company.strengths_of_partner_financial_institutions}
                    />
                  )}
                  {proposalData?.agent?.company?.strengths_of_properties_for_management && (
                    <OfferDetailContent
                      title="販売後の物件管理"
                      content={proposalData.agent.company.strengths_of_properties_for_management}
                    />
                  )}
                  {proposalData?.agent?.company?.appeal_point && (
                    <OfferDetailContent
                      title="自社ならではのアピールポイント"
                      content={proposalData.agent.company.appeal_point}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* CTA */}
            {isMatchButtonVisible && (
              <Box className={classes.button}>
                <CustomButton
                  customVariant="filled"
                  onClick={handleClickModalApprove}
                  disabled={proposalData?.is_matched}
                  withArrow={!proposalData?.is_matched}
                >
                  {proposalData?.is_matched ? "マッチング済み" : PLANNER_MATCH_CTA_LABEL}
                </CustomButton>
              </Box>
            )}
          </Box>
        </>
      )}
      {isApproveModalOpen && (
        <OfferApproveModal
          offerType="assessment"
          agentId={approvedAgentId}
          onClose={() => setIsApproveModalOpen(false)}
          onSubmitPreferredContactSchedules={async (request) => {
            if (!validId) return false;

            // assessment のレスポンスには日程フィールドが無く表示に反映されないため、
            // 成功後の再取得はしない（investment 側は表示に反映されるため対象外）
            return updatePreferredContactSchedules("assessment", numId, request);
          }}
        />
      )}
    </BaseTemplate>
  );
};
